import {
  getVisitorLabel,
  WebchatConversation,
} from '@/services/webchat-service/webchat.service.types';
import { useWebchatStore } from '@/store/webchat.store';
import { Badge, Tag } from 'antd';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import clsx from 'clsx';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { getConversationChannel } from '@/utils/conversation-channel';
import { OnHoldIndicator } from '../on-hold-indicator/on-hold-indicator';
import styles from './session-card.module.scss';

dayjs.extend(relativeTime);

interface SessionCardProps {
  session: WebchatConversation;
}

// Wrapped in React.memo so a re-render of the parent SessionList (which
// fires every time the `sessions` array gets a new reference — i.e.,
// every WS event) doesn't propagate into every card. Cards now only
// re-render when their own session object's identity changes OR when
// one of their three per-session Zustand selector subscriptions
// (selected / typing / disconnected) flips for THIS session.
const SessionCardComponent = ({ session }: SessionCardProps) => {
  const { t } = useTranslation('webchat');
  // Subscribing without a selector re-renders this card on *every* store
  // mutation — including unrelated transcript turns, typing flips for
  // other sessions, and bridgeStatus updates. With 3+ queued cards each
  // repainting on every WebSocket event, the cumulative re-render storm
  // was the dominant source of the "whole-page flicker" the user
  // reported. Narrow to the three slices this card actually reads, and
  // re-derive booleans locally so the subscription is on the per-session
  // primitives only.
  const isSelected = useWebchatStore((s) => s.selectedConversationId === session.id);
  const isTyping = useWebchatStore((s) => Boolean(s.visitorTyping[session.id]));
  const isDisconnected = useWebchatStore((s) => s.disconnectedVisitors.has(session.id));
  const setSelectedConversationId = useWebchatStore((s) => s.setSelectedConversationId);

  const isIdle =
    session.webchat_status === 'live' &&
    session.idle_since &&
    dayjs().diff(dayjs(session.idle_since), 'minute') >= 2;
  const idleMinutes = isIdle ? dayjs().diff(dayjs(session.idle_since), 'minute') : 0;

  const visitorLabel = getVisitorLabel(session);
  const channel = getConversationChannel(session.type);
  const ChannelIcon = channel.icon as React.ComponentType<{ style?: React.CSSProperties }>;
  // Chat is the implicit default — don't bother tagging it. Every other
  // channel gets a small icon + label so the rep instantly sees what they're
  // looking at without reading deeper.
  const showChannelTag = session.type && session.type !== 'ChatConversation';
  const lastMsg = session.last_message;
  const preview =
    lastMsg?.sender_type === 'visitor'
      ? lastMsg.translated_text || lastMsg.original_text
      : lastMsg?.original_text;
  const truncatedPreview = preview ? (preview.length > 50 ? `${preview.slice(0, 50)}...` : preview) : '';

  return (
    <div
      className={clsx(styles.card, { [styles.selected]: isSelected })}
      onClick={() => setSelectedConversationId(session.id)}
      data-testid="session-card"
      data-conversation-id={session.id}
      data-conversation-type={session.type}
      data-webchat-status={session.webchat_status}
      data-visitor-phone={session.visitor_phone}
    >
      <div className={styles.topRow}>
        <span className={styles.visitorName}>
          {showChannelTag && (
            <Tag color={channel.color} style={{ marginRight: 4 }}>
              <ChannelIcon style={{ marginRight: 4 }} />
              {channel.label}
            </Tag>
          )}
          {session.returning_caller && (
            <Tag color="geekblue" style={{ marginRight: 4 }}>{t('sessionCard.returning')}</Tag>
          )}
          {visitorLabel}
        </span>
        <span className={styles.time}>{dayjs(session.idle_since).fromNow()}</span>
      </div>
      {isTyping ? (
        <div className={styles.typingText}>{t('sessionCard.typing')}</div>
      ) : (
        truncatedPreview && <div className={styles.preview}>{truncatedPreview}</div>
      )}
      <div className={styles.bottomRow}>
        {session.unread_count > 0 && <Badge count={session.unread_count} size="small" />}
        {/*
         * On-hold badge only for rai voice calls (AnalyticsCall) that
         * have been routed to the Command Center queue. Chat / SMS /
         * email queues don't have a "hold music" concept; ElevenLabsCall
         * uses :live during its pre-claim window, not :queued.
         */}
        {session.type === 'AnalyticsCall' && session.webchat_status === 'queued' && (
          <OnHoldIndicator since={session.idle_since} />
        )}
        {isIdle && <Tag color="orange">{t('sessionCard.idle', { count: idleMinutes })}</Tag>}
        {isDisconnected && <Tag color="red">{t('visitor.disconnected')}</Tag>}
        <span className={styles.conversationId}>#{session.id}</span>
      </div>
    </div>
  );
};

export const SessionCard = memo(SessionCardComponent);
